import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useLocation, useNavigate } from "react-router";
import { useSidebar } from "../../../../../context/useSidebar";
import { LAYOUT } from "../../../../../shared/layout";
import SessionAddBasicForm from "./SessionAddBasicForm";
import SessionAddUploadNotesForm from "./SessionAddUploadNotesForm";
import SessionAddUploadAudioForm from "./SessionAddUploadAudioForm";
import SessionAddNotesForm from "./SessionAddNotesForm";
import SessionAddAIAnalysisForm from "./SessionAddAIAnalysisForm";
import SessionAddChooseForm from "./SessionAddChooseForm";
import SessionAddLiveForm from "./SessionAddLiveForm";
import SessionAddVideoCallForm from "./SessionAddVideoCallForm";
import { sessionService } from "../../../application/SessionApplicationService";
import { settleWrittenNotes } from "../../../application/sessionUseCases";
import { getSessionTemplate } from "../../../application/sessionTemplates";
import { Session, SessionAnalysis } from "../../../domain/Session";
import { isUpcoming } from "../../../domain/sessionExtras";
import { plainTextToHtml } from "../../utils/notesUtils";
import { analyzeErrorMessage } from "../../utils/analyzeErrorMessage";
import RecommendationsPanel from "../RecommendationsPanel";
import SessionAIChatPanel from "./SessionAIChatPanel";
import SessionToolsPanel from "./SessionToolsPanel";
import { useSubscription } from "../../../../subscription";
import { useAiInsightsGate } from "../../../../ai_insights";
import type { SessionTemplate } from "../../../../session_templates";
import PatientHighlightsTab from "../PatientHighlightsTab";
import { Sparkles, Activity, X, MessageSquare, Highlighter, Puzzle } from "lucide-react";
import { LiveInsightsProvider } from "../../../../../context/LiveInsightsContext";
import { useLiveInsightsContext } from "../../../../../context/LiveInsightsContextShared";
import { LiveInsightsDashboard } from "../live-insights/LiveInsightsDashboard";
import { useTherapist } from "../../../../../context/TherapistProvider";
import { useBreakpoint } from "../../../../../hooks/useBreakpoint";
import { StepProgress } from "../../../../../shared/ui/primitives";
import SessionDockButton from "./SessionDockButton";
import { toLocalDateKey } from "../../../../../shared/utils/date";
import { subjectSessionsPath } from "../../../domain/sessionsPath";

interface SessionFormProps {
  therapistID: string;
}

type SessionType =
  | "live"
  | "image_notes"
  | "manual_notes"
  | "upload_audio"
  | "video_call"
  | "alchely_meeting";

export default function SessionAddForm({ therapistID }: SessionFormProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { collapseSidebar } = useSidebar();
  const { isPremium } = useSubscription();
  const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
  const aiLocked = !adoptionLoading && !aiInsightsAdopted;
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionType, setSessionType] = useState<SessionType | null>(null);

  const [analysis, setAnalysis] = useState<SessionAnalysis | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | null>(null);
  const [rightPanelRefreshKey, setRightPanelRefreshKey] = useState(0);
  const [leftWidth, setLeftWidth] = useState(70); // Default width percentage for the left panel
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activePanel, setActivePanel] = useState<'recommendations' | 'live-insights' | 'ai-chat' | 'highlights' | 'tools' | null>(null);
  const { isMobile, isTablet, breakpoint } = useBreakpoint();
  const { refreshTherapist } = useTherapist();

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newWidth = ((mouseMoveEvent.clientX - containerRect.left) / containerRect.width) * 100;

        // Boundaries: 30% to 85%
        if (newWidth > 30 && newWidth < 85) {
          setLeftWidth(newWidth);
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  useEffect(() => {
    if (location.state?.startLive && location.state?.sessionId) {
      setSessionId(location.state.sessionId);
      setSessionType("live");
      setCurrentStep(3);
      collapseSidebar();
    }
  }, [location.state, collapseSidebar]);

  useEffect(() => {
    if (location.state?.startVideoCall && location.state?.sessionId) {
      setSessionId(location.state.sessionId);
      setSessionType("video_call");
      setCurrentStep(3);
      collapseSidebar();
    }
  }, [location.state, collapseSidebar]);

  /**
   * The generic form of the two shortcuts above: a caller that already knows
   * which mode it wants (the quick-session modal) hands the session and the
   * mode over, and the wizard opens on that mode's step.
   */
  useEffect(() => {
    const requested = location.state?.sessionType as SessionType | undefined;
    if (!requested || !location.state?.sessionId) return;
    setSessionId(location.state.sessionId);
    setSessionType(requested);
    setCurrentStep(3);
    // Recording needs the room the sidebar takes; the other modes do not.
    if (requested === "live" || requested === "video_call") {
      collapseSidebar();
    }
  }, [location.state, collapseSidebar]);

  const [basicData, setBasicData] = useState({
    date: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD in local time
    patient: "",
    couple_patient_id: undefined as string | undefined,
    group_patient_id: undefined as string | undefined,
    therapy_type: "",
    title: "",
    hour: (() => {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    })(),
    ubication: "",
    type: "individual",
    duration: 60,
    template_id: undefined as string | undefined,
  });

  useEffect(() => {
    const fetchSessionData = async () => {
      if (sessionId && !basicData.patient && !basicData.couple_patient_id && !basicData.group_patient_id) {
        try {
          const existing = await sessionService.getSessionById(sessionId);
          if (existing) {
            setBasicData(prev => ({
              ...prev,
              patient: existing.patient_id || "",
              couple_patient_id: existing.couple_patient_id,
              group_patient_id: existing.group_patient_id,
              date: existing.date ? toLocalDateKey(new Date(existing.date)) : prev.date,
              title: existing.title || prev.title,
              template_id: existing.template_id || prev.template_id,
              therapy_type: existing.therapy_type || prev.therapy_type,
              hour: existing.hour || prev.hour,
              ubication: existing.ubication || prev.ubication,
              type: existing.type || prev.type,
              duration: existing.duration || prev.duration,
            }));
          }
        } catch (err) {
          console.error("Error fetching session details:", err);
        }
      }
    };

    fetchSessionData();
  }, [sessionId, basicData.patient, basicData.couple_patient_id, basicData.group_patient_id]);


  // Fetch template details when template_id changes
  useEffect(() => {
    const fetchTemplateDetails = async () => {
      if (basicData.template_id) {
        try {
          const tpl = await getSessionTemplate(basicData.template_id);
          setSelectedTemplate(tpl);
        } catch (err) {
          console.error("Error fetching template details:", err);
        }
      } else {
        setSelectedTemplate(null);
      }
    };
    fetchTemplateDetails();
  }, [basicData.template_id]);

  // Where leaving the wizard goes: the sessions of whoever it is with.
  const exitPath = subjectSessionsPath({
    patient_id: basicData.patient || null,
    couple_patient_id: basicData.couple_patient_id,
    group_patient_id: basicData.group_patient_id,
  });
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---------------------------
   * Steps config
   * --------------------------- */

  // Without the AI Insights tool there is no analysis step at the end of the wizard.
  const totalSteps = (sessionType === "manual_notes" ? 4 : 5) - (aiLocked ? 1 : 0);
  // video_call skips step 4 (notes form) and goes straight to analysis, same as live

  /* ---------------------------
   * Step handlers
   * --------------------------- */

  const handleStep1Continue = async () => {
    setLoading(true);
    setError(null);

    try {
      const created = await sessionService.createSession({
        date: basicData.date,
        title: basicData.title,
        patient_id: basicData.type === "individual" ? (basicData.patient || undefined) : undefined,
        couple_patient_id: basicData.type === "couple" ? (basicData.couple_patient_id || undefined) : undefined,
        group_patient_id: basicData.type === "group" ? (basicData.group_patient_id || undefined) : undefined,
        therapy_type: basicData.therapy_type,
        therapist_id: therapistID,
        hour: basicData.hour,
        ubication: basicData.ubication,
        type: basicData.type as "individual" | "couple" | "group",
        duration: basicData.duration || 60, // Default to 60 if 0
        template_id: basicData.template_id,
        notes: "",
      });

      setSessionId(created.id);
      setCurrentStep(2);
      refreshTherapist();
    } catch {
      setError(t("session.errorCreating")); // Assuming I can add this key or reuse error key
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const updateData: Partial<Session> = {
        notes,
        template_id: basicData.template_id
      };

      await sessionService.updateSession(sessionId, updateData);

      // Written or photographed notes are not an analysis: the session now
      // waits for one (or is closed, saying why none can run). Marking it
      // `completed` here made it read as analyzed when nothing had been.
      if (sessionType === "manual_notes" || sessionType === "image_notes") {
        await settleWrittenNotes(sessionId);
      }

      // image_notes keeps the transcribed text verbatim — no template is applied.

      if (aiLocked) {
        // No analysis step: the session is done, go straight to its detail view.
        navigate(`/session/${sessionId}`);
        return;
      }

      setCurrentStep(totalSteps); // Go to last step (Analysis)
    } catch {
      setError(t("session.errorSavingNotes"));
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await sessionService.requestAnalysis(sessionId);
      setAnalysis(result);
      setRightPanelRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error analyzing session:", err);
      setError(analyzeErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------
   * Render
   * --------------------------- */


  return (
    <LiveInsightsProvider sessionId={sessionId || ""}>
      <SessionAddFormInner
        sessionId={sessionId}
        exitPath={exitPath}
        meetingLink={location.state?.meetingLink ?? null}
        currentStep={currentStep}
        totalSteps={totalSteps}
        aiLocked={aiLocked}
        aiInsightsAdopted={aiInsightsAdopted}
        basicData={basicData}
        setBasicData={setBasicData}
        handleStep1Continue={handleStep1Continue}
        sessionType={sessionType}
        setSessionType={setSessionType}
        setNotes={setNotes}
        setCurrentStep={setCurrentStep}
        handleSaveNotes={handleSaveNotes}
        selectedTemplate={selectedTemplate}
        analysis={analysis}
        handleAnalyze={handleAnalyze}
        loading={loading}
        error={error}
        t={t}
        therapistID={therapistID}
        notes={notes}
        isPremium={isPremium}
        rightPanelRefreshKey={rightPanelRefreshKey}
        leftWidth={leftWidth}
        setLeftWidth={setLeftWidth}
        isResizing={isResizing}
        startResizing={startResizing}
        containerRef={containerRef}
        activePanel={activePanel}
        setActivePanel={setActivePanel}
        isMobile={isMobile}
        isTablet={isTablet}
        breakpoint={breakpoint}
      />
    </LiveInsightsProvider>
  );
}

interface BasicData {
  date: string;
  patient: string;
  couple_patient_id: string | undefined;
  group_patient_id: string | undefined;
  therapy_type: string;
  title: string;
  hour: string;
  ubication: string;
  type: string;
  duration: number;
  template_id: string | undefined;
}

interface InnerProps {
  /** Where leaving the wizard goes: the sessions of whoever it is with. */
  exitPath: string;
  sessionId: string | null;
  /** Only set when the wizard was opened from a scheduled online session. */
  meetingLink: string | null;
  currentStep: number;
  totalSteps: number;
  aiLocked: boolean;
  /** Confirmed adoption of Memoria aumentada. Distinct from `aiLocked`, which
   *  is false while adoption is still loading: a dock button that appears and
   *  then vanishes is worse than one that arrives a moment late. */
  aiInsightsAdopted: boolean;
  basicData: BasicData;
  setBasicData: React.Dispatch<React.SetStateAction<BasicData>>;
  handleStep1Continue: () => Promise<void>;
  sessionType: SessionType | null;
  setSessionType: React.Dispatch<React.SetStateAction<SessionType | null>>;
  setNotes: React.Dispatch<React.SetStateAction<string>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  handleSaveNotes: () => Promise<void>;
  selectedTemplate: SessionTemplate | null;
  analysis: SessionAnalysis | null;
  handleAnalyze: () => Promise<void>;
  loading: boolean;
  error: string | null;
  t: TFunction;
  therapistID: string;
  notes: string;
  isPremium: boolean;
  rightPanelRefreshKey: number;
  leftWidth: number;
  setLeftWidth: React.Dispatch<React.SetStateAction<number>>;
  isResizing: boolean;
  startResizing: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  activePanel: 'recommendations' | 'live-insights' | 'ai-chat' | 'highlights' | 'tools' | null;
  setActivePanel: React.Dispatch<React.SetStateAction<'recommendations' | 'live-insights' | 'ai-chat' | 'highlights' | 'tools' | null>>;
  isMobile: boolean;
  isTablet: boolean;
  breakpoint: string;
}

function SessionAddFormInner({
  sessionId,
  exitPath,
  meetingLink,
  currentStep,
  totalSteps,
  aiLocked,
  aiInsightsAdopted,
  basicData,
  setBasicData,
  handleStep1Continue,
  sessionType,
  setSessionType,
  setNotes,
  setCurrentStep,
  handleSaveNotes,
  selectedTemplate,
  analysis,
  handleAnalyze,
  loading,
  error,
  t,
  therapistID,
  notes,
  isPremium,
  rightPanelRefreshKey,
  leftWidth,
  isResizing,
  startResizing,
  containerRef,
  activePanel,
  setActivePanel,
  isMobile,
  isTablet,
  breakpoint,
}: InnerProps) {
  useLiveInsightsContext();
  useTherapist();
  const navigate = useNavigate();

  const handlePanelToggle = (panel: 'recommendations' | 'live-insights' | 'ai-chat' | 'highlights' | 'tools') => {
    setActivePanel(prev => (prev === panel ? null : panel));
  };

  // Somebody the session is with: a patient, a couple or a group — memory works for all three.
  const hasPatientSelected = basicData.patient || basicData.couple_patient_id || basicData.group_patient_id;
  const isGroupSession = basicData.type === "group" && Boolean(basicData.group_patient_id);
  const showLiveInsightsDock = false; // Hidden for now as it is a future feature
  const isTabletOrLargePortrait = isTablet || breakpoint === 'lg';

  const aiPanelsAvailable = Boolean(hasPatientSelected) && isPremium && aiInsightsAdopted;
  const dockButtons = ([
    { panel: 'recommendations' as const, icon: <Sparkles className="size-5" />, label: t("recommendations.title"), show: aiPanelsAvailable },
    { panel: 'ai-chat' as const, icon: <MessageSquare className="size-5" />, label: t("patient.tabs.aiChat"), show: aiPanelsAvailable },
    { panel: 'highlights' as const, icon: <Highlighter className="size-5" />, label: t("session.highlights", "Highlights"), show: Boolean(hasPatientSelected) && isPremium },
    { panel: 'tools' as const, icon: <Puzzle className="size-5" />, label: t("session.tools", "Tools"), show: Boolean(hasPatientSelected) },
    { panel: 'live-insights' as const, icon: <Activity className="size-5" />, label: t("session.liveInsights"), show: showLiveInsightsDock },
  ]).filter((button) => button.show);

  return (
    <div
      ref={containerRef}
      className={`flex w-full select-none ${isMobile || isTabletOrLargePortrait ? 'flex-col min-h-screen pb-20' : 'h-[calc(100vh-8rem)]'}`}
      style={{ cursor: !isMobile && !isTabletOrLargePortrait && isResizing ? 'col-resize' : 'default' }}
    >
      {/* FORM AREA */}
      <div
        className="space-y-6 overflow-y-auto pr-2 custom-scrollbar flex-1"
        style={{ width: !isMobile && !isTabletOrLargePortrait && activePanel ? `${leftWidth}%` : '100%' }}
      >
        <StepProgress
          current={currentStep}
          total={totalSteps}
          label={t("session.step", { current: currentStep, total: totalSteps })}
        />

        {/* STEP 1 */}
        {currentStep === 1 && (
          <SessionAddBasicForm
            therapistID={therapistID}
            data={basicData}
            onChange={(newData) =>
              setBasicData((prev: BasicData) => ({
                ...prev,
                ...newData,
                hour: (newData as Partial<BasicData>).hour ?? prev.hour,
                ubication: (newData as Partial<BasicData>).ubication ?? prev.ubication,
                type: (newData as Partial<BasicData>).type ?? prev.type,
                duration: (newData as Partial<BasicData>).duration ?? prev.duration,
                template_id: (newData as Partial<BasicData>).template_id !== undefined ? (newData as Partial<BasicData>).template_id : prev.template_id,
              }))
            }
            onContinue={handleStep1Continue}
          />
        )}

        {/* STEP 2 */}
        {currentStep === 2 && (
          <SessionAddChooseForm
            onSelect={async (type) => {
              // The call gets its own tab rather than a step: the wizard stays
              // put, so the therapist still has somewhere to come back to.
              if (type === "alchely_meeting" && sessionId) {
                window.open(`/meet/session/${sessionId}`, "_blank", "noopener");
                return;
              }
              setSessionType(type as SessionType);
              setCurrentStep(3);
            }}
            onBack={() => setCurrentStep(1)}
            // A session dated ahead is kept pending, to be prepared and
            // written up once it has taken place.
            onSaveForLater={
              sessionId && isUpcoming(basicData.date) ? () => navigate(`/session/${sessionId}`) : undefined
            }
          />
        )}

        {/* STEP 3 — by type */}
        {currentStep === 3 && sessionType === "image_notes" && sessionId && (
          <SessionAddUploadNotesForm
            sessionId={sessionId}
            onExtractedText={(text) => setNotes(plainTextToHtml(text))}
            onContinue={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 3 && sessionType === "upload_audio" && sessionId && (
          <SessionAddUploadAudioForm
            sessionId={sessionId}
            exitPath={exitPath}
            onExtractedText={setNotes}
            onContinue={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 3 && sessionType === "live" && sessionId && (
          <SessionAddLiveForm
            sessionId={sessionId}
            exitPath={exitPath}
            onContinue={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 3 && sessionType === "video_call" && sessionId && (
          <SessionAddVideoCallForm
            sessionId={sessionId}
            exitPath={exitPath}
            meetingLink={meetingLink}
            onContinue={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 3 && sessionType === "manual_notes" && (
          <SessionAddNotesForm
            notes={notes}
            onNotesChange={setNotes}
            onSave={handleSaveNotes}
            onBack={() => setCurrentStep(2)}
            template={selectedTemplate}
            onTemplateChange={(tid) => setBasicData((prev: BasicData) => ({ ...prev, template_id: tid }))}
          />
        )}

        {/* STEP 4 — final notes (live / image / audio) */}
        {currentStep === 4 && sessionType !== "manual_notes" && (
          <SessionAddNotesForm
            notes={notes}
            onNotesChange={setNotes}
            onSave={handleSaveNotes}
            onBack={() => setCurrentStep(3)}
            /* image_notes keeps the transcription verbatim: passing the template
               here would rebuild the notes as an empty template document. */
            template={sessionType === "image_notes" ? null : selectedTemplate}
            onTemplateChange={(tid) => setBasicData((prev: BasicData) => ({ ...prev, template_id: tid }))}
            allowTemplateChange={false}
          />
        )}

        {/* STEP 4/5 — AI analysis */}
        {!aiLocked && currentStep === totalSteps && currentStep >= 4 && (
          <SessionAddAIAnalysisForm
            analysis={analysis}
            onAnalyze={handleAnalyze}
            loading={loading}
            sessionId={sessionId}
          />
        )}

        {/* Errors */}
        {error && (
          <p className="text-red-600 text-center text-sm">{error}</p>
        )}
      </div>

      {/* Resize Handle */}
      {!isMobile && !isTabletOrLargePortrait && activePanel && (
        <div
          onMouseDown={startResizing}
          className={`group flex items-center justify-center w-4 cursor-col-resize transition-all ${isResizing ? 'bg-accent-tint' : 'hover:bg-gray-50/50'}`}
        >
          <div className={`w-0.5 h-12 rounded-full transition-all ${isResizing ? 'bg-accent' : 'bg-gray-200 group-hover:bg-gray-300'}`} />
        </div>
      )}

      {/* Side Panel Content */}
      {activePanel && (
        <div
          className={`${isMobile 
            ? 'fixed inset-0 z-50 bg-white flex flex-col p-4 pb-24' 
            : isTabletOrLargePortrait
            ? 'fixed top-0 right-0 h-full w-3/4 z-50 bg-white shadow-2xl flex flex-col p-6 pb-24 border-l animate-in slide-in-from-right duration-300'
            : 'min-w-0 h-full flex flex-col p-4'}`}
          style={{ width: isMobile ? '100%' : isTabletOrLargePortrait ? '75%' : `${100 - leftWidth}%` }}
        >
          <div className="flex-1 overflow-hidden relative">
            <button
              onClick={() => setActivePanel(null)}
              className="absolute top-4 right-4 p-2 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-500 shadow-sm border border-gray-100 transition-all z-30 flex items-center justify-center"
              style={{ minHeight: LAYOUT.TOUCH_TARGET_SIZE, minWidth: LAYOUT.TOUCH_TARGET_SIZE }}
            >
              <X className="w-5 h-5" />
            </button>
            {activePanel === 'recommendations' && (
              <RecommendationsPanel
                patientId={isGroupSession ? basicData.group_patient_id! : basicData.type === "couple" ? basicData.couple_patient_id! : basicData.patient}
                subjectKind={isGroupSession ? "group" : basicData.type === "couple" ? "couple" : "patient"}
                refreshKey={rightPanelRefreshKey}
                className="!w-full !h-full !sticky-0"
              />
            )}
            {activePanel === 'live-insights' && (
              <LiveInsightsDashboard fullPage={isMobile} />
            )}
            {basicData.type === "couple" && basicData.couple_patient_id && (
              <div className={activePanel === 'ai-chat' ? undefined : 'hidden'}>
                <SessionAIChatPanel couplePatientId={basicData.couple_patient_id} />
              </div>
            )}
            {basicData.type === "individual" && basicData.patient && (
              <div className={activePanel === 'ai-chat' ? undefined : 'hidden'}>
                <SessionAIChatPanel patientId={basicData.patient} />
              </div>
            )}
            {isGroupSession && (
              <div className={activePanel === 'ai-chat' ? undefined : 'hidden'}>
                <SessionAIChatPanel groupPatientId={basicData.group_patient_id} />
              </div>
            )}
            {activePanel === 'highlights' && (
              <div className="h-full overflow-y-auto pt-12 custom-scrollbar">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 px-1">
                  {t("session.highlights", "Highlights")}
                </h3>
                {isGroupSession ? (
                  <PatientHighlightsTab patientId={basicData.group_patient_id!} isGroup={true} />
                ) : basicData.type === "couple" && basicData.couple_patient_id ? (
                  <PatientHighlightsTab patientId={basicData.couple_patient_id} isCouple={true} />
                ) : basicData.patient ? (
                  <PatientHighlightsTab patientId={basicData.patient} />
                ) : (
                  <p className="text-sm text-content-tertiary px-1">
                    {t("session.selectPatientFirst", "Select a patient first")}
                  </p>
                )}
              </div>
            )}
            {activePanel === 'tools' && (
              <SessionToolsPanel
                sessionId={sessionId}
                session={{ type: basicData.type, couple_patient_id: basicData.couple_patient_id }}
                subject={
                  basicData.type === "group" && basicData.group_patient_id
                    ? { kind: "group", id: basicData.group_patient_id }
                    : basicData.type === "couple" && basicData.couple_patient_id
                      ? { kind: "couple", id: basicData.couple_patient_id }
                      : basicData.patient
                        ? { kind: "patient", id: basicData.patient }
                        : null
                }
              />
            )}
          </div>
        </div>
      )}

      {/* Dock: a rail beside the form on desktop, a bar below it on small screens. */}
      {dockButtons.length > 0 && (
        <div
          aria-label={t("session.panels", "Session panels")}
          role="toolbar"
          className={
            isMobile || isTabletOrLargePortrait
              ? "fixed bottom-20 left-0 right-0 z-40 flex h-16 items-center justify-around border-t border-border-subtle glass-effect px-4"
              : "flex w-16 flex-col items-center gap-3 border-l border-border-subtle bg-surface-base py-6"
          }
        >
          {dockButtons.map((button) => (
            <SessionDockButton
              key={button.panel}
              icon={button.icon}
              label={button.label}
              active={activePanel === button.panel}
              onClick={() => handlePanelToggle(button.panel)}
              compact={isMobile || isTabletOrLargePortrait}
            />
          ))}
        </div>
      )}
    </div>
  );
}
